"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Solutions() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const commissions = [{
    number: "SERVICE / 01",
    name: "Strategic Consulting",
    description: "Precision-engineered advisory services designed to navigate complex market landscapes. We align your operational goals with long-term vision, ensuring every decision is backed by data and industry foresight.",
    scale: "Enterprise-wide",
    lead: "4–8 weeks",
    investment: "From $12,000",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/strategic_consulting.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/strategic_consulting.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/strategic_consulting.webp-wvc-sizes",
    alt: "Professional strategic consulting session in a modern office",
    imageLeft: true
  }, {
    number: "SERVICE / 02",
    name: "Project Management",
    description: "End-to-end oversight of your most critical initiatives. We manage the friction between concept and execution, maintaining rigorous standards of quality and timeline adherence throughout the project lifecycle.",
    scale: "Multi-phase projects",
    lead: "Ongoing",
    investment: "From $8,500/mo",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp-wvc-sizes",
    alt: "Project management dashboard and collaborative workspace",
    imageLeft: false
  }, {
    number: "SERVICE / 03",
    name: "Operational Optimization",
    description: "Refining the internal mechanics of your business to eliminate waste and amplify output. We audit existing workflows and implement lean, scalable systems that adapt to your evolving market demands.",
    scale: "Departmental audit",
    lead: "6–10 weeks",
    investment: "From $18,000",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/operational_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/operational_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/operational_optimization.webp-wvc-sizes",
    alt: "Abstract representation of operational efficiency and workflow",
    imageLeft: true
  }, {
    number: "SERVICE / 04",
    name: "Bespoke Support",
    description: "Tailored assistance for high-stakes professional requirements. Whether it is specialized research or executive-level coordination, we provide the focused expertise necessary to maintain your competitive edge.",
    scale: "Individual to team",
    lead: "Flexible",
    investment: "From $3,200",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/bespoke_support.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/bespoke_support.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/bespoke_support.webp-wvc-sizes",
    alt: "Professional support and coordination tools",
    imageLeft: false
  }];
  return <section data-section-id="4783" id="tailored-solutions" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 light">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div ref={header.ref} className={`transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary mb-10 md:mb-16">
            — 01 / TAILORED SOLUTIONS
          </p>
          <h2 className="font-default font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground max-w-6xl">
            <span className="block">From strategy</span>
            <span className="block">
              to{" "}
              <span className="italic font-normal text-primary">
                measurable results.
              </span>
            </span>
          </h2>
        </div>

        <Separator className="mt-20 md:mt-28 bg-border" />

        {}
        <div>
          {commissions.map((item, i) => <CommissionBlock key={i} data-index={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}
function CommissionBlock({
  item,
  index,
  ...rest
}: {
  item: {
    number: string;
    name: string;
    description: string;
    scale: string;
    lead: string;
    investment: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    imageLeft: boolean;
  };
  index: number;
  [key: string]: unknown;
}) {
  const block = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest}>
      <div ref={block.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 lg:gap-16 py-20 md:py-28 lg:py-32 transition-all duration-1000 ease-out ${block.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className={`lg:col-span-6 ${item.imageLeft ? "lg:order-1" : "lg:order-2"}`}>
          <div className="relative w-full overflow-hidden bg-muted rounded-lg shadow-md">
            <div className="aspect-[4/5]">
              <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105" />
            </div>
          </div>
        </div>

        {}
        <div className={`lg:col-span-6 flex items-center ${item.imageLeft ? "lg:order-2" : "lg:order-1"}`}>
          <div className="w-full lg:px-8 xl:px-12 2xl:px-16 py-4">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-8 md:mb-10">
              {item.number}
            </p>

            <h3 className="font-default font-light text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-tight text-foreground mb-8 md:mb-10">
              {item.name}
            </h3>

            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mb-10 md:mb-12 max-w-xl">
              {item.description}
            </p>

            {}
            <div className="mb-10 md:mb-12 max-w-xl">
              <SpecRow label="Typical scale" value={item.scale} />
              <SpecRow label="Lead time" value={item.lead} />
              <SpecRow label="Starting investment" value={item.investment} />
              <Separator className="bg-border" />
            </div>

            {}
            <Button asChild variant="link" className="px-0 h-auto font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary hover:no-underline group">
              <Link to="/ConnectPage">
                Discuss this service
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <Separator className="bg-border" />
    </div>;
}
function SpecRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <>
      <Separator className="bg-border" />
      <div className="grid grid-cols-2 gap-4 py-4 md:py-5">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <span className="font-default text-sm md:text-base text-foreground text-right">
          {value}
        </span>
      </div>
    </>;
}